import React, { useEffect, useState } from "react";
import {
    generategroupid,
    getrestrictions,
    settingcreate,
    settinglist,
    updaterestrictions,
} from "../../services/setting";
import { branchlist } from "../../services/master";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

/**
 * One setting per row: name on the left, control right-aligned on the right.
 * The divider is on the top edge rather than the bottom so the first row sits
 * flush against the card header and the last row needs no special case.
 */
const SettingRow = ({ label, htmlFor, children }) => (
    <div className="row align-items-center py-3 border-top">
        <div className="col-md-6">
            <label className="form-label mb-0 fw-semibold" htmlFor={htmlFor}>
                {label}
            </label>
        </div>
        <div className="col-md-6 text-md-end mt-2 mt-md-0">{children}</div>
    </div>
);

/**
 * Inline checkbox set used by the frequency, loan-type and KYC rows.
 * `items` is [label, checked, setter] so it reads the same at each call site.
 */
const CheckGroup = ({ name, items }) => (
    <>
        {items.map(([label, value, setter]) => (
            <div className="form-check form-check-inline" key={label}>
                <input
                    className="form-check-input"
                    type="checkbox"
                    id={`${name}-${label}`}
                    checked={value}
                    onChange={(e) => setter(e.target.checked)}
                />
                <label className="form-check-label" htmlFor={`${name}-${label}`}>
                    {label}
                </label>
            </div>
        ))}
    </>
);

export const Settings = () => {
    const [group, setGroup] = useState("Automatic");
    const [groupId, setGroupId] = useState("");
    const [grpprf, setGrpPrf] = useState("");
    const [grpsuf, setGrpSuf] = useState("");

    // Which branch the Plan Limits card is editing. "" is the default that
    // every branch without its own settings follows. Branch Max Limit and the
    // push message templates are whole-system and only editable there.
    const [restrictionBranch, setRestrictionBranch] = useState("");
    const [blist, setBlist] = useState([]);
    // Branch ids that have been given their own settings, so the dropdown can
    // say which ones are no longer following the default.
    const [overriddenBranches, setOverriddenBranches] = useState([]);
    const [hasOverride, setHasOverride] = useState(false);

    // System limits. Blank means unlimited, matching the backend.
    const [maxBranches, setMaxBranches] = useState("");
    const [maxGroups, setMaxGroups] = useState("");
    const [maxMembers, setMaxMembers] = useState("");
    const [allowDaily, setAllowDaily] = useState(true);
    const [allowWeekly, setAllowWeekly] = useState(true);
    const [allowMonthly, setAllowMonthly] = useState(true);
    const [allowGroupLoans, setAllowGroupLoans] = useState(true);
    const [allowIndividualLoans, setAllowIndividualLoans] = useState(true);
    const [allowSms, setAllowSms] = useState(false);
    const [allowPush, setAllowPush] = useState(true);
    // Drives how Interest Per Due is entered on the Add Scheme screen.
    const [schemeInterestType, setSchemeInterestType] = useState("amount");
    // Verify buttons on the member screens. Each call is a paid lookup.
    const [allowAadhaar, setAllowAadhaar] = useState(true);
    const [allowPan, setAllowPan] = useState(true);
    const [allowBank, setAllowBank] = useState(true);

    // Per-type push notification message templates. Each entry is
    // { key, label, title, vars: [], value } loaded from the backend so the
    // labels and available {variables} stay in sync with what the server sends.
    const [pushTemplates, setPushTemplates] = useState([]);

    useEffect(() => {
        branchlist().then((res) => {
            if (res?.status === true && Array.isArray(res.data)) {
                setBlist(res.data);
            }
        });
    }, []);

    // Re-read whenever the branch being edited changes, so the card always
    // shows that branch's own settings (or the default it inherits).
    useEffect(() => {
        loadRestrictions(restrictionBranch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restrictionBranch]);

    function loadRestrictions(branchId) {
        getrestrictions(branchId).then((res) => {
            if (res?.status === true && res.data?.details) {
                const d = res.data.details;
                setMaxBranches(d.max_branches ?? "");
                setMaxGroups(d.max_groups_per_branch ?? "");
                setMaxMembers(d.max_members_per_group ?? "");
                setAllowDaily(!!d.allow_daily);
                setAllowWeekly(!!d.allow_weekly);
                setAllowMonthly(!!d.allow_monthly);
                setAllowGroupLoans(!!d.allow_group_loans);
                setAllowIndividualLoans(!!d.allow_individual_loans);
                setAllowSms(!!d.allow_sms);
                setAllowPush(d.allow_push === undefined || d.allow_push === null ? true : !!d.allow_push);
                setSchemeInterestType(d.scheme_interest_type ?? "amount");
                setAllowAadhaar(d.allow_aadhaar_verification ?? true);
                setAllowPan(d.allow_pan_verification ?? true);
                setAllowBank(d.allow_bank_verification ?? true);
                setHasOverride(!!d.has_branch_override);
                if (Array.isArray(res.data.branches_with_override)) {
                    setOverriddenBranches(res.data.branches_with_override);
                }
                if (Array.isArray(res.data.push_templates)) {
                    setPushTemplates(res.data.push_templates);
                }
            }
        });
    }

    function setPushTemplateValue(key, value) {
        setPushTemplates((list) =>
            list.map((t) => (t.key === key ? { ...t, value } : t))
        );
    }

    function submitRestrictions(e) {
        e.preventDefault();

        if (!allowDaily && !allowWeekly && !allowMonthly) {
            toast.error("Enable at least one collection frequency.");
            return;
        }

        const temp = new FormData();
        if (restrictionBranch !== "") {
            temp.append("branch_id", restrictionBranch);
        } else {
            // Whole-system, so only saved on the default.
            temp.append("max_branches", maxBranches === "" ? "" : maxBranches);
        }
        temp.append("max_groups_per_branch", maxGroups === "" ? "" : maxGroups);
        temp.append("max_members_per_group", maxMembers === "" ? "" : maxMembers);
        temp.append("allow_daily", allowDaily ? 1 : 0);
        temp.append("allow_weekly", allowWeekly ? 1 : 0);
        temp.append("allow_monthly", allowMonthly ? 1 : 0);
        temp.append("allow_group_loans", allowGroupLoans ? 1 : 0);
        temp.append("allow_individual_loans", allowIndividualLoans ? 1 : 0);
        temp.append("allow_sms", allowSms ? 1 : 0);
        temp.append("allow_push", allowPush ? 1 : 0);
        temp.append("scheme_interest_type", schemeInterestType);
        temp.append("allow_aadhaar_verification", allowAadhaar ? 1 : 0);
        temp.append("allow_pan_verification", allowPan ? 1 : 0);
        temp.append("allow_bank_verification", allowBank ? 1 : 0);
        // Per-type push message templates. Whole-system, so only sent on the
        // default; the backend ignores them for a branch either way.
        if (restrictionBranch === "") {
            pushTemplates.forEach((t) => temp.append(t.key, t.value ?? ""));
        }

        updaterestrictions(temp).then((res) => {
            if (res?.status === true) {
                toast.success(res.message);
                // A branch saved for the first time now has its own settings.
                loadRestrictions(restrictionBranch);
            } else if (res?.message) {
                toast.error(res.message);
            }
        });
    }

    /**
     * The push message wording is whole-system, so it always saves against the
     * default and on its own — never carrying the branch-wise toggles above it,
     * which may be showing a single branch's values.
     */
    function submitPushTemplates(e) {
        e.preventDefault();

        const temp = new FormData();
        pushTemplates.forEach((t) => temp.append(t.key, t.value ?? ""));

        updaterestrictions(temp).then((res) => {
            if (res?.status === true) {
                toast.success(res.message);
            } else if (res?.message) {
                toast.error(res.message);
            }
        });
    }

    /**
     * Puts a branch back on the default settings by deleting the row that was
     * saved for it, so later changes to the default reach it again.
     */
    function resetBranchRestrictions() {
        const branchName =
            blist.find((b) => String(b.id) === String(restrictionBranch))?.branch_name ||
            "this branch";

        Swal.fire({
            title: `Reset ${branchName} to the default settings?`,
            text: "Its own restrictions will be removed and it will follow the default again.",
            showCancelButton: true,
            confirmButtonColor: "#d71635",
            confirmButtonText: "Reset",
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }

            const temp = new FormData();
            temp.append("branch_id", restrictionBranch);
            temp.append("reset", 1);

            updaterestrictions(temp).then((res) => {
                if (res?.status === true) {
                    toast.success(res.message);
                    setOverriddenBranches((ids) =>
                        ids.filter((id) => String(id) !== String(restrictionBranch))
                    );
                    loadRestrictions(restrictionBranch);
                } else if (res?.message) {
                    toast.error(res.message);
                }
            });
        });
    }

    useEffect(() => {
        settinglist().then((res) => {
            if (res.status === true) {
                if (res.data !== null) {
                    setGroup(res.data.group);
                    setGrpPrf(res.data.group_prefix);
                    setGroupId(res.data.group_number);
                    setGrpSuf(res.data.group_suffix);

                } else {
                    handlegroup({ target: { value: "Automatic" } });
                }
            } else {
                handlegroup({ target: { value: "Automatic" } });
            }
        });
    }, []);

    function handlegroup(e) {
        setGroup(e.target.value);
        setGroupId("");
        setGrpPrf("");
        setGrpSuf("");
        if (e.target.value === "Automatic") {
            generategroupid().then((res) => {
                if (res.status === true) {
                    if (res.data !== null) {
                        setGrpPrf(res.data.group_prefix);
                        setGroupId(res.data.group_number);
                        setGrpSuf(res.data.group_suffix);
                    }
                }
            });
        } else {
            setGroupId("");
            setGrpPrf("");
            setGrpSuf("");
        }
    }

    function handlesubmit(e) {
        e.preventDefault();
        const temp = new FormData();
        temp.append("group_prefix", grpprf);
        temp.append("group_number", groupId);
        temp.append("group_suffix", grpsuf ? grpsuf : "");
        temp.append("group", group);
        settingcreate(temp).then((res) => {
            if (res.status === true) {
                toast.success(res.message);
            }
        });
    }

    return (
        <div>
            <div className="page-header">
                <h2 className="main-content-title tx-24 mg-b-5">Settings</h2>
                {/* <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a>Settings</a>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Settings
          </li>
        </ol> */}
            </div>
            <div className="content-body">
                <div className="row">
                    <div className="col-xl-12">
                        <div className="card">
                            <div className="card-header">
                                <h2 className="main-content-title tx-24 mg-b-5">Settings</h2>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                Group<span className="text-danger"> *</span>
                                            </label>
                                            <select
                                                className="form-control"
                                                aria-label="Default select example"
                                                value={group}
                                                onChange={(e) => handlegroup(e)}
                                            >
                                                <option value="" selected disabled>
                                                    Select Group
                                                </option>
                                                <option value="Manual">Manual</option>
                                                <option value="Automatic">Automatic</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            {/* <label className="form-label">Group ID <span className="text-danger"> *</span></label> */}
                                            <div className="row">
                                                <div className="col-md-4">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            Prefix <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Enter Prefix"
                                                            value={grpprf}
                                                            onChange={(e) => setGrpPrf(e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-md-4">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            Number <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Enter Number"
                                                            value={groupId}
                                                            onChange={(e) => setGroupId(e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-md-4">
                                                    <div className="mb-3">
                                                        <label className="form-label">
                                                            Suffix 
                                                            {/* <span className="text-danger">*</span> */}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Enter Suffix"
                                                            value={grpsuf}
                                                            onChange={(e) => setGrpSuf(e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {/* <input type="text" className="form-control" placeholder="Group ID" value={groupId} onChange={(e) => setGroupId(e.target.value)} /> */}
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-12 text-right">
                                        <a
                                            type="submit"
                                            className="btn btn-success"
                                            onClick={handlesubmit}
                                        >
                                            Submit
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Plan limits, enforced on the branch, group, member and loan
                    screens. Anything switched off here surfaces to the user as
                    "not included in your current plan". */}
                <div className="row">
                    <div className="col-xl-12">
                        <div className="card">
                            <div className="card-header d-md-flex align-items-center justify-content-between">
                                <h2 className="main-content-title tx-24 mg-b-5">
                                    Plan Limits
                                </h2>
                                {/* Everything below Branch Max Limit is set per
                                    branch. "Default" is what a branch follows
                                    until it is given settings of its own. */}
                                <div className="d-flex align-items-center">
                                    <label
                                        className="form-label mb-0 me-2 fw-semibold text-nowrap"
                                        htmlFor="restriction-branch"
                                    >
                                        Applies To
                                    </label>
                                    <select
                                        id="restriction-branch"
                                        className="form-control d-inline-block w-auto"
                                        value={restrictionBranch}
                                        onChange={(e) => setRestrictionBranch(e.target.value)}
                                    >
                                        <option value="">Default (All Branches)</option>
                                        {blist.map((b) => (
                                            <option value={b.id} key={b.id}>
                                                {b.branch_name}
                                                {overriddenBranches.some(
                                                    (id) => String(id) === String(b.id)
                                                )
                                                    ? " (custom)"
                                                    : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="card-body pt-0">
                                <p className="tx-12 text-muted pt-3 mb-0">
                                    {restrictionBranch === "" ? (
                                        <>
                                            These are the default limits. Every branch follows them
                                            unless you pick a branch above and save different ones.
                                        </>
                                    ) : hasOverride ? (
                                        <>
                                            This branch has its own limits. Changes to the default no
                                            longer reach it — use Reset To Default to put it back.
                                        </>
                                    ) : (
                                        <>
                                            This branch is currently following the default limits
                                            shown below. Saving here gives it its own.
                                        </>
                                    )}
                                </p>

                                {/* Whole-system: it caps how many branches may
                                    exist at all, so there is nothing per-branch
                                    to set. */}
                                {restrictionBranch === "" && (
                                    <SettingRow label="Branch Max Limit" htmlFor="max-branches">
                                        <input
                                            id="max-branches"
                                            type="number"
                                            min="0"
                                            className="form-control d-inline-block w-auto"
                                            placeholder="Unlimited"
                                            value={maxBranches}
                                            onChange={(e) => setMaxBranches(e.target.value)}
                                        />
                                    </SettingRow>
                                )}

                                <SettingRow label="Group Max Limit" htmlFor="max-groups">
                                    <input
                                        id="max-groups"
                                        type="number"
                                        min="0"
                                        className="form-control d-inline-block w-auto"
                                        placeholder="Unlimited"
                                        value={maxGroups}
                                        onChange={(e) => setMaxGroups(e.target.value)}
                                    />
                                </SettingRow>

                                <SettingRow label="Members Max Limit" htmlFor="max-members">
                                    <input
                                        id="max-members"
                                        type="number"
                                        min="0"
                                        className="form-control d-inline-block w-auto"
                                        placeholder="Unlimited"
                                        value={maxMembers}
                                        onChange={(e) => setMaxMembers(e.target.value)}
                                    />
                                </SettingRow>

                                <SettingRow label="Collection Frequency Allowed">
                                    <CheckGroup
                                        name="freq"
                                        items={[
                                            ["Daily", allowDaily, setAllowDaily],
                                            ["Weekly", allowWeekly, setAllowWeekly],
                                            ["Monthly", allowMonthly, setAllowMonthly],
                                        ]}
                                    />
                                </SettingRow>

                                <SettingRow label="Loans Allowed">
                                    <CheckGroup
                                        name="loan"
                                        items={[
                                            ["Group Loans", allowGroupLoans, setAllowGroupLoans],
                                            [
                                                "Individual Loans",
                                                allowIndividualLoans,
                                                setAllowIndividualLoans,
                                            ],
                                        ]}
                                    />
                                </SettingRow>

                                <SettingRow label="KYC Verification Allowed">
                                    <CheckGroup
                                        name="kyc"
                                        items={[
                                            ["Aadhaar", allowAadhaar, setAllowAadhaar],
                                            ["PAN", allowPan, setAllowPan],
                                            ["Bank", allowBank, setAllowBank],
                                        ]}
                                    />
                                </SettingRow>

                                <SettingRow label="SMS" htmlFor="allow-sms">
                                    <select
                                        id="allow-sms"
                                        className="form-control d-inline-block w-auto"
                                        value={allowSms ? "yes" : "no"}
                                        onChange={(e) => setAllowSms(e.target.value === "yes")}
                                    >
                                        <option value="no">No</option>
                                        <option value="yes">Yes</option>
                                    </select>
                                </SettingRow>

                                <SettingRow label="Push Notifications" htmlFor="allow-push">
                                    <select
                                        id="allow-push"
                                        className="form-control d-inline-block w-auto"
                                        value={allowPush ? "yes" : "no"}
                                        onChange={(e) => setAllowPush(e.target.value === "yes")}
                                    >
                                        <option value="no">No</option>
                                        <option value="yes">Yes</option>
                                    </select>
                                </SettingRow>

                                <SettingRow
                                    label="Scheme Interest Per Due"
                                    htmlFor="scheme-interest-type"
                                >
                                    <select
                                        id="scheme-interest-type"
                                        className="form-control d-inline-block w-auto"
                                        value={schemeInterestType}
                                        onChange={(e) => setSchemeInterestType(e.target.value)}
                                    >
                                        <option value="amount">Amount</option>
                                        <option value="percentage">Percentage</option>
                                    </select>
                                </SettingRow>

                                <div className="row border-top pt-3">
                                    <div className="col-md-12 text-end">
                                        {restrictionBranch !== "" && hasOverride && (
                                            <a
                                                className="btn btn-outline-danger me-2"
                                                onClick={resetBranchRestrictions}
                                            >
                                                Reset To Default
                                            </a>
                                        )}
                                        <a
                                            type="submit"
                                            className="btn btn-success"
                                            onClick={submitRestrictions}
                                        >
                                            {restrictionBranch === ""
                                                ? "Save Plan Limits"
                                                : "Save For This Branch"}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Message template per push type. Each box customises the body
                    of the automatic notification the system sends for that event;
                    {variables} are replaced with real values at send time. Saved
                    with the plan limits above (same Save Plan Limits button). */}
                {pushTemplates.length > 0 && (
                    <div className="row">
                        <div className="col-xl-12">
                            <div className="card">
                                <div className="card-header">
                                    <h2 className="main-content-title tx-24 mg-b-5">
                                        Push Notification Messages
                                    </h2>
                                </div>
                                <div className="card-body">
                                    <p className="tx-12 text-muted mb-3">
                                        Customise the message for each notification type. Leave a
                                        box blank to use the default. Words in curly braces like{" "}
                                        <code>{"{amount}"}</code> are replaced automatically when
                                        the notification is sent. The wording is the same for every
                                        branch — only whether push is switched on is set per branch.
                                    </p>
                                    <div className="row">
                                        {pushTemplates.map((t) => (
                                            <div className="col-md-6 mb-3" key={t.key}>
                                                <label
                                                    className="form-label fw-semibold"
                                                    htmlFor={`tpl-${t.key}`}
                                                >
                                                    {t.label}
                                                </label>
                                                {t.vars && t.vars.length > 0 && (
                                                    <p className="tx-12 text-muted mb-1">
                                                        Variables: <code>{t.vars.join(", ")}</code>
                                                    </p>
                                                )}
                                                <textarea
                                                    id={`tpl-${t.key}`}
                                                    className="form-control"
                                                    rows="3"
                                                    value={t.value ?? ""}
                                                    onChange={(e) =>
                                                        setPushTemplateValue(t.key, e.target.value)
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="row border-top pt-3">
                                        <div className="col-md-12 text-end">
                                            <a
                                                type="submit"
                                                className="btn btn-success"
                                                onClick={submitPushTemplates}
                                            >
                                                Save Messages
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
